import { useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { useRouter } from '@src/routes'
import { sendNewSrpTabSwitchEvent } from '@src/utils/analytics'
import { useResponsive } from '@src/utils/useResponsiveSizes'
import { SearchType, SearchFocusType } from '@utils/searchUtils'
import { useSearchQuery } from '@src/pages/[country]/[language]/search/[searchTerm]'
import NewProductSearchResult from './SearchResults/Products/NewProductSearchResult'
import ContentSearchResults from './SearchResults/Content/ContentSearchResults'
import { useSearchContext } from './SearchContext'
import CommonTabs from '@src/components/Common/CommonTabs'

const useStyles = makeStyles()((theme) => ({
  vr8: {
    marginBottom: theme.spacing(8),
  },
  mobileContainer: {
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  desktopContainer: {
    marginTop: theme.spacing(4),
  },
}))

const SearchTabs = () => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  const { isMobile } = useResponsive()
  const router = useRouter()
  const selectedTab =
    router?.query?.focus === SearchType.B2B
      ? SearchType.Products
      : router?.query?.focus || SearchType.Products
  const { metadata } = useSearchContext()
  const [, , { handleFocusChange }] = useSearchQuery()

  const tabPanels = [
    {
      value: SearchType.Products,
      label: formatMessage({ id: 'PRODUCTS', defaultMessage: 'Products' }),
      component: <NewProductSearchResult />,
      gaLabel: 'products',
    },
    {
      value: SearchType.SiteContent,
      label: formatMessage({
        id: 'SITE_CONTENT',
        defaultMessage: 'Site Content',
      }),
      component: (
        <ContentSearchResults searchType={SearchFocusType.SiteContent} />
      ),
      gaLabel: 'site content',
    },
    {
      value: SearchType.TechnicalDocuments,
      label: formatMessage({
        id: 'SUPPORTING_DOCUMENTS',
        defaultMessage: 'Supporting Documents',
      }),
      component: (
        <ContentSearchResults searchType={SearchFocusType.TechnicalDocuments} />
      ),
      gaLabel: 'supporting documents',
    },
  ]

  const handleChange = (_: any, newValue: string) => {
    const { facet, searchTerm } = router?.query
    const facetCount = Array.isArray(facet) ? facet.length : undefined
    const facets = facet
      ? Array.isArray(facet)
        ? facet.join(',')
        : facet
      : undefined

    sendNewSrpTabSwitchEvent({
      originalTab: tabPanels.find((tab) => tab.value === selectedTab)?.gaLabel,
      newTab: tabPanels.find((tab) => tab.value === newValue)?.gaLabel,
      searchTerm: searchTerm || '',
      facets: facets ?? undefined,
      totalCount: metadata?.count,
      searchResultDisplay: `page ${metadata?.page} - - ${metadata?.perPage} results per page`,
      facetCount,
    })

    handleFocusChange(newValue.toLowerCase())
  }

  return (
    <div
      className={isMobile ? classes.mobileContainer : classes.desktopContainer}
      data-testid="search-result-tabs"
    >
      <CommonTabs
        tabs={tabPanels}
        activeTabIndex={tabPanels.findIndex((tab) => tab.value === selectedTab)}
        changeCallback={(newIndex) => {
          const newValue = tabPanels[newIndex]?.value
          if (newValue) {
            handleChange(newIndex, newValue)
          }
        }}
      />
    </div>
  )
}

export default SearchTabs
